/**
 * Meta (Facebook) Pixel E-Commerce Event Tracking
 *
 * Fires standard Meta Pixel events for retargeting and conversion tracking.
 * All functions are safe to call even if fbq is not available.
 */

declare global {
  interface Window {
    fbq?: (...args: any[]) => void;
  }
}

function isFbqAvailable(): boolean {
  return typeof window !== 'undefined' && typeof window.fbq === 'function';
}

/** Track a product page view */
export function fbTrackViewContent(product: {
  id: string | number;
  name: string;
  category?: string;
  price?: number;
}) {
  if (!isFbqAvailable()) return;
  window.fbq!('track', 'ViewContent', {
    content_ids: [String(product.id)],
    content_name: product.name,
    content_category: product.category || '',
    content_type: 'product',
    value: product.price || 0,
    currency: 'USD',
  });
}

/** Track add to cart */
export function fbTrackAddToCart(product: {
  id: string | number;
  name: string;
  category?: string;
  price?: number;
  quantity?: number;
}) {
  if (!isFbqAvailable()) return;
  window.fbq!('track', 'AddToCart', {
    content_ids: [String(product.id)],
    content_name: product.name,
    content_type: 'product',
    value: (product.price || 0) * (product.quantity || 1),
    currency: 'USD',
  });
}

/** Track checkout initiation */
export function fbTrackInitiateCheckout(params: {
  value: number;
  numItems: number;
  contentIds?: string[];
}) {
  if (!isFbqAvailable()) return;
  window.fbq!('track', 'InitiateCheckout', {
    value: params.value,
    currency: 'USD',
    num_items: params.numItems,
    content_ids: params.contentIds || [],
    content_type: 'product',
  });
}

/** Track completed purchase */
export function fbTrackPurchase(params: {
  value: number;
  orderId?: string;
  contentIds?: string[];
  numItems?: number;
}) {
  if (!isFbqAvailable()) return false;
  const value = Number(params.value);
  if (!Number.isFinite(value)) return false;
  // eventID = order number, so a repeat of the same order's Purchase (reload,
  // second tab, or a future Conversions API send using the same id) is
  // deduplicated by Meta instead of counted twice.
  const options = params.orderId ? { eventID: `purchase_${params.orderId}` } : undefined;
  window.fbq!('track', 'Purchase', {
    value,
    currency: 'USD',
    content_ids: params.contentIds || [],
    content_type: 'product',
    num_items: params.numItems || 1,
    ...(params.orderId ? { order_id: String(params.orderId) } : {}),
  }, ...(options ? [options] : []));
  return true;
}

/** Track search */
export function fbTrackSearch(query: string) {
  if (!isFbqAvailable()) return;
  window.fbq!('track', 'Search', {
    search_string: query,
  });
}

/** Track lead (e.g., email signup, sample request) */
export function fbTrackLead(params?: { value?: number; category?: string; eventId?: string }) {
  if (!isFbqAvailable()) return;
  window.fbq!('track', 'Lead', {
    value: params?.value || 0,
    currency: 'USD',
    content_category: params?.category || '',
  }, ...(params?.eventId ? [{ eventID: params.eventId }] : []));
}

/** Track a contact action (e.g. a tap on a phone number). Meta standard event. */
export function fbTrackContact(params?: { method?: string; source?: string }) {
  if (!isFbqAvailable()) return;
  window.fbq!('track', 'Contact', {
    content_category: params?.method || 'phone',
    content_name: params?.source || '',
  });
}

/** Track custom event */
export function fbTrackCustom(eventName: string, params?: Record<string, any>) {
  // A blank name reaches Meta as "__missing_event"; never send one.
  if (!isFbqAvailable() || typeof eventName !== 'string' || !eventName.trim()) return;
  window.fbq!('trackCustom', eventName, params || {});
}
